import { useMemo } from 'react';

import { VerticalContainer } from '@american-autonomy/ag-ui';

import Grid from 'components/Grid';

import useData from './hooks/useData';
import { DEFAULT_RATE_UNIT } from './hooks/useData/constants';
import useTranslation from './hooks/useTranslation';

import PageHeader from './components/PageHeader';
import PrescriptionImportPageModals from './components/PrescriptionImportPageModals';
import SideRail from './components/SideRail';
import UploadCard from './components/UploadCard';
import { ACCEPT_FILE_TYPES, TEST_ID } from './constants';
import { buildSideRailProps, buildUploadCardLabels, computeFilledData } from './pageHelpers';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const PrescriptionImportPage: FunctionComponent<Props> = ({ testId = TEST_ID }) => {
    const t = useTranslation();
    const data = useData();

    const isFilled = data.uploadState.kind === 'readyToSubmit';
    const filledData = computeFilledData({ uploadState: data.uploadState });
    const labels = useMemo(() => buildUploadCardLabels(t), [t]);
    const railProps = useMemo(
        () => buildSideRailProps({ filledData, isFilled, t }),
        [filledData, isFilled, t]
    );

    return (
        <VerticalContainer className={styles.prescriptionImportPage} testId={testId}>
            <VerticalContainer className={styles.main}>
                <VerticalContainer className={styles.content}>
                    <PageHeader
                        eyebrow={t.PAGE_EYEBROW}
                        subtitle={t.PAGE_SUBTITLE}
                        title={t.PAGE_TITLE}
                    />

                    <Grid className={styles.layout}>
                        <UploadCard
                            acceptFileTypes={ACCEPT_FILE_TYPES}
                            approxAreaHectares={filledData.approxAreaHectares}
                            errorMessage={data.errorMessage}
                            fileName={filledData.fileName}
                            gapPolicy={data.gapPolicy}
                            isFilled={isFilled}
                            isProcessing={data.isProcessing}
                            isSubmitting={data.isSubmitting}
                            labels={labels}
                            onCancel={data.handleCancelPage}
                            onFilesSelected={data.handleFilesSelected}
                            onGapPolicyChange={data.handleGapPolicyChange}
                            onReplaceFile={data.handleCancelUpload}
                            onSubmit={data.handleSubmit}
                            rateColumn={filledData.rateColumn}
                            rateUnit={DEFAULT_RATE_UNIT}
                            zones={filledData.zones}
                        />

                        <SideRail
                            boundaryEmptyBody={railProps.boundaryEmptyBody}
                            boundaryStats={railProps.boundaryStats}
                            boundaryTitle={railProps.boundaryTitle}
                            nextStepsBody={railProps.nextStepsBody}
                            nextStepsTitle={railProps.nextStepsTitle}
                        />
                    </Grid>
                </VerticalContainer>
            </VerticalContainer>

            <PrescriptionImportPageModals
                cancelText={t.WARNING_DISMISS}
                columnPickerHint={t.RATE_COLUMN_HINT}
                columnPickerTitle={t.RATE_COLUMN_LABEL}
                columns={data.columns}
                confirmText={t.RATE_COLUMN_CONFIRM}
                isColumnPickerOpen={data.isColumnPickerOpen}
                isWarningOpen={data.isWarningOpen}
                onCancelColumnPicker={data.handleCancelUpload}
                onColumnSelected={data.handleColumnSelected}
                onWarningDismiss={data.handleWarningDismiss}
                warningMessage={data.warningMessage}
                warningTitle={t.WARNING_TITLE}
            />
        </VerticalContainer>
    );
};

export default PrescriptionImportPage;
